
import { VITE_BACKEND_URL } from "../../api/config";
import type {
  RevenueActivityData,
  YearlyCollection,
  CollaboratorsData,
  TrimestreEconomico,
  DonatedBeneficiariesData,
} from "./revenueActivity.types";

function readNumber(value: unknown): number {
  const amount =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : NaN;

  if (!Number.isFinite(amount)) {
    throw new Error("Uno de los importes recibidos no es válido.");
  }

  return amount;
}

function readNonNegativeAmount(value: unknown): number {
  const amount = readNumber(value);

  if (amount < 0) {
    throw new Error("El importe recibido no es válido.");
  }

  return amount;
}

function readNullableAmount(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }

  return readNumber(value);
}


function readQuarter(item: unknown): TrimestreEconomico {
  if (typeof item !== "object" || item === null) {
    throw new Error("Los datos trimestrales no son válidos.");
  }

  const data = item as Record<string, unknown>;

  const anio = readNumber(data.anio);
  const trimestre = readNumber(data.trimestre);

  if (
    !Number.isSafeInteger(anio) ||
    !Number.isSafeInteger(trimestre) ||
    trimestre < 1 ||
    trimestre > 4
  ) {
    throw new Error("El trimestre recibido no es válido.");
  }

  const estados = ["Pendiente", "Presentado", "Regularizado"];

  if (
    typeof data.estado !== "string" ||
    !estados.includes(data.estado)
  ) {
    throw new Error("El estado trimestral no es válido.");
  }

  return {
    anio,
    trimestre,
    ingresos_brutos: readNonNegativeAmount(data.ingresos_brutos),
    iva_regularizado: readNullableAmount(data.iva_regularizado),
    otros_impuestos_hacienda: readNullableAmount(
      data.otros_impuestos_hacienda,
    ),
    gastos_empresa: readNullableAmount(data.gastos_empresa),
    estado: data.estado as TrimestreEconomico["estado"],
  };
}

function readYearlyCollection(item: unknown): YearlyCollection {
  if (
    typeof item !== "object" ||
    item === null ||
    !("year" in item) ||
    typeof item.year !== "number" ||
    !Number.isSafeInteger(item.year)
  ) {
    throw new Error("El histórico de recaudación no es válido.");
  }

  const data = item as Record<string, unknown>;

  const trimestres = Array.isArray(data.trimestres)
    ? data.trimestres.map(readQuarter)
    : [];

  return {
    year: item.year,
    totalCollected: readNonNegativeAmount(data.totalCollected),
    ivaDescontado: readNullableAmount(data.ivaDescontado),
    ingresosSinIVA: readNullableAmount(data.ingresosSinIVA),
    otrosImpuestosHacienda: readNullableAmount(
      data.otrosImpuestosHacienda,
    ),
    gastosEmpresa: readNullableAmount(data.gastosEmpresa),
    beneficioNeto: readNullableAmount(data.beneficioNeto),
    objetivoDonacion: readNullableAmount(data.objetivoDonacion),
    totalDonations: readNonNegativeAmount(data.totalDonations),
    pendienteDonar: readNullableAmount(data.pendienteDonar),

    ivaRegularizado: readNullableAmount(data.ivaRegularizado),
    ivaAjusteAnual: readNullableAmount(data.ivaAjusteAnual),

    otrosImpuestosTrimestrales: readNullableAmount(
      data.otrosImpuestosTrimestrales,
    ),
    otrosImpuestosAjusteAnual: readNullableAmount(
      data.otrosImpuestosAjusteAnual,
    ),

    gastosTrimestrales: readNullableAmount(data.gastosTrimestrales),
    gastosAjusteAnual: readNullableAmount(data.gastosAjusteAnual),

    datosFiscalesCompletos: data.datosFiscalesCompletos === true,
    trimestres,
  };
}

function getBackendUrl(): string {
  const backendUrl = VITE_BACKEND_URL?.replace(/\/+$/, "");

  if (!backendUrl) {
    throw new Error("La URL del backend no está configurada.");
  }

  return backendUrl;
}

export async function getRevenueActivity(
  signal?: AbortSignal,
): Promise<RevenueActivityData> {
  const response = await fetch(
    `${getBackendUrl()}/api/actividad-recaudacion`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error("No se ha podido consultar la recaudación.");
  }

  const data: unknown = await response.json();

  if (
    typeof data !== "object" ||
    data === null ||
    !("currentYear" in data) ||
    typeof data.currentYear !== "number" ||
    !Number.isSafeInteger(data.currentYear) ||
    !("yearlyCollections" in data) ||
    !Array.isArray(data.yearlyCollections)
  ) {
    throw new Error("La respuesta de recaudación no es válida.");
  }

  const root = data as Record<string, unknown>;
  const yearlyCollections = data.yearlyCollections.map(
    readYearlyCollection,
  );

  return {
    currentYear: data.currentYear,
    totalCollected: readNonNegativeAmount(root.totalCollected),
    ivaDescontado: readNullableAmount(root.ivaDescontado),
    ingresosSinIVA: readNullableAmount(root.ingresosSinIVA),
    otrosImpuestosHacienda: readNullableAmount(
      root.otrosImpuestosHacienda,
    ),
    gastosEmpresa: readNullableAmount(root.gastosEmpresa),
    beneficioNeto: readNullableAmount(root.beneficioNeto),
    objetivoDonacion: readNullableAmount(root.objetivoDonacion),
    totalDonations: readNonNegativeAmount(root.totalDonations),
    pendienteDonar: readNullableAmount(root.pendienteDonar),
    datosFiscalesCompletos: root.datosFiscalesCompletos === true,
    yearlyCollections,
  };
}

export async function getCollaborators(
  signal?: AbortSignal,
): Promise<CollaboratorsData> {
  const response = await fetch(
    `${getBackendUrl()}/api/actividad-recaudacion/colaboradores`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error("No se han podido consultar los colaboradores.");
  }

  const data: unknown = await response.json();

  if (
    typeof data !== "object" ||
    data === null ||
    !("total" in data) ||
    typeof data.total !== "number" ||
    !("collaborators" in data) ||
    !Array.isArray(data.collaborators)
  ) {
    throw new Error("La respuesta de colaboradores no es válida.");
  }

  const collaborators = data.collaborators.map((item) => {
    if (
      typeof item !== "object" ||
      item === null ||
      !("nombre" in item) ||
      typeof item.nombre !== "string"
    ) {
      throw new Error("La lista de colaboradores no es válida.");
    }

    return { nombre: item.nombre };
  });

  return {
    total: data.total,
    collaborators,
  };
}

export async function getDonatedBeneficiaries(
  signal?: AbortSignal,
): Promise<DonatedBeneficiariesData> {
  const response = await fetch(
    `${getBackendUrl()}/api/actividad-recaudacion/beneficiarios`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
      signal,
    },
  );

  if (!response.ok) {
    throw new Error("No se han podido consultar las donaciones realizadas.");
  }

  const data: unknown = await response.json();

  if (
    typeof data !== "object" ||
    data === null ||
    !("total" in data) ||
    typeof data.total !== "number" ||
    !Number.isSafeInteger(data.total) ||
    data.total < 0 ||
    !("ayudas" in data) ||
    !Array.isArray(data.ayudas)
  ) {
    throw new Error("La respuesta de donaciones no es válida.");
  }

  const ayudas = data.ayudas.map((item): DonatedBeneficiariesData["ayudas"][number] => {
    if (typeof item !== "object" || item === null) {
      throw new Error("Uno de los beneficiarios recibidos no es válido.");
    }

    const record = item as Record<string, unknown>;

    if (
      typeof record.donacion_id !== "number" ||
      !Number.isSafeInteger(record.donacion_id) ||
      typeof record.importe !== "number" ||
      !Number.isFinite(record.importe) ||
      record.importe < 0 ||
      typeof record.fecha !== "string" ||
      typeof record.tipo !== "string" ||
      typeof record.nombre !== "string" ||
      (record.descripcion_publica !== null &&
        typeof record.descripcion_publica !== "string") ||
      (record.localidad_publica !== null &&
        typeof record.localidad_publica !== "string") ||
      (record.concepto_publico !== null &&
        typeof record.concepto_publico !== "string")
    ) {
      throw new Error("Los datos de una donación no son válidos.");
    }

    return {
      donacion_id: record.donacion_id,
      importe: record.importe,
      fecha: record.fecha,
      tipo: record.tipo,
      nombre: record.nombre,
      descripcion_publica: record.descripcion_publica,
      localidad_publica: record.localidad_publica,
      concepto_publico: record.concepto_publico,
    };
  });

  return {
    total: data.total,
    ayudas,
  };
}
